import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { apiClient } from '@/lib/apiClient'

import { usePublicEvent } from './api'

// L'équipe et les écrans de service ne sont pas l'audience du site.
const NOT_AUDIENCE = ['/admin', '/ops', '/ecran']

// Un signe de vie par minute tant que l'onglet est visible : c'est ce qui
// fait le « en ce moment » de l'accueil. La fenêtre côté serveur (cinq
// minutes par défaut) en laisse passer plusieurs avant d'oublier quelqu'un.
const HEARTBEAT_MS = 60_000

function isAudience(pathname: string): boolean {
  return !NOT_AUDIENCE.some((prefix) => pathname.startsWith(prefix)) && navigator.doNotTrack !== '1'
}

/**
 * Compte une page vue du site public à chaque changement de rubrique, et
 * signale chaque minute qu'un onglet visible est toujours ouvert.
 *
 * Aucun cookie, rien d'écrit sur l'appareil : le serveur compte un visiteur
 * par jour à partir d'une empreinte qu'il ne conserve pas en clair. Un
 * navigateur qui demande « ne pas me suivre » est respecté. Un échec réseau
 * est ignoré : une statistique ne vaut pas une erreur affichée.
 */
export function PageViewTracker() {
  const { pathname } = useLocation()
  const event = usePublicEvent()
  const slug = event.data?.slug
  const first = useRef(true)

  useEffect(() => {
    if (!isAudience(pathname)) return
    // On attend l'édition pour rattacher la visite — sauf si elle ne vient pas.
    if (!slug && event.isPending) return

    const referrer = first.current ? document.referrer || null : null
    first.current = false
    apiClient
      .post('/public/page-views', { path: pathname, referrer, event_slug: slug ?? null })
      .catch(() => undefined)
  }, [pathname, slug, event.isPending])

  // La page vue compte déjà comme signe de vie : le battement prend le relais
  // une minute plus tard. Un onglet caché se tait (l'intervalle est arrêté,
  // pas seulement ignoré) ; il se signale de nouveau dès qu'on y revient.
  useEffect(() => {
    if (!isAudience(pathname)) return
    let timer: number | undefined
    const beat = () => {
      apiClient.post('/public/presence', { path: pathname }).catch(() => undefined)
    }
    const stop = () => {
      if (timer !== undefined) window.clearInterval(timer)
      timer = undefined
    }
    const start = () => {
      stop()
      timer = window.setInterval(beat, HEARTBEAT_MS)
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        beat()
        start()
      } else {
        stop()
      }
    }
    if (document.visibilityState === 'visible') start()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [pathname])

  return null
}
